/**
 * VqeTooltip — the single shadcn-Tooltip wrapper used across the Visual Query
 * Editor. It replaces the native `title=` attribute so reveal delay, styling and
 * touch behavior match the rest of the app.
 *
 * Two ergonomics it bakes in so callers don't have to repeat them:
 *  - The trigger is wrapped in a real-box `<span>` (via `asChild`) so a
 *    *disabled* control still surfaces the tooltip. The shadcn `<Button disabled>`
 *    base class sets `disabled:pointer-events-none`, so the disabled child emits
 *    NO pointer events; the wrapper therefore needs its OWN box (not
 *    `display:contents`, which has no box) to receive hover. The default
 *    `inline-flex` tight-wraps inline controls; pass `block` for full-width /
 *    block regions (e.g. a drop-zone or values `<div>`) so the wrapper stays
 *    layout-neutral and fills its slot instead of shrinking to content.
 *  - `selfProvider` mounts a local `<TooltipProvider>` so the component is
 *    renderable outside the app-root provider (AddButton / RemoveButton are
 *    explicitly designed to render in isolation, e.g. unit tests).
 *
 * When `content` is falsy the children render bare — callers can keep their
 * existing conditional ("show tooltip only while disabled") by passing
 * `undefined`, exactly mirroring the old `title={cond ? text : undefined}`.
 */

import type { ReactNode } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../../components/ui/tooltip';

export interface VqeTooltipProps {
  /** Tooltip copy. When empty/undefined the children render without a tooltip. */
  readonly content?: ReactNode;
  /** The control the tooltip describes. */
  readonly children: ReactNode;
  /** Preferred side; defaults to the Radix default ("top"). */
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
  /** Mount a local provider so the tooltip works outside the app-root one. */
  readonly selfProvider?: boolean;
  /**
   * Use a full-width, layout-neutral wrapper instead of the tight `inline-flex`
   * one. Pass this when the wrapped child needs to fill its slot rather than
   * collapse to content width: a block `<div>` drop-zone / values strip, or a
   * `flex-1 truncate` value cell inside a flex row. `block w-full min-w-0`
   * keeps the child full-width in block flow AND lets the wrapper grow-and-
   * shrink (so the inner `truncate` still clips) when it sits in a flex row.
   */
  readonly block?: boolean;
  /** Allow the tight inline wrapper to shrink inside a flex row (adds `min-w-0`)
   *  so an inner `truncate` can engage. Opt-in: default wrappers keep
   *  `min-width:auto` so small icon controls are never crushed. Ignored when
   *  `block` is set (that variant already carries `min-w-0`). */
  readonly shrink?: boolean;
}

export function VqeTooltip({ content, children, side = 'top', selfProvider, block, shrink }: VqeTooltipProps) {
  if (!content) return <>{children}</>;

  // A real box (not `display:contents`) is required so the wrapper receives
  // hover even when the child is `pointer-events:none` (disabled buttons).
  // `inline-flex` tight-wraps inline controls; `block w-full min-w-0` keeps
  // full-width block regions (drop-zone / values / flex-1 cells) layout-neutral.
  const wrapperClass = block ? 'block w-full min-w-0' : shrink ? 'inline-flex min-w-0' : 'inline-flex';

  const tip = (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={wrapperClass}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side={side}>{content}</TooltipContent>
    </Tooltip>
  );

  return selfProvider ? <TooltipProvider>{tip}</TooltipProvider> : tip;
}
